import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/ui/use-toast';
import { Loader2, Plus, Minus, Award, Users, Smartphone, Apple } from 'lucide-react';

interface CustomerLoyaltyInfo {
  customer_id: string;
  customer_name?: string;
  program_name: string;
  program_type: 'points' | 'stamps';
  current_points: number;
  current_stamps: number;
  total_visits: number;
  business_id: string;
  wallet_type?: 'apple' | 'google';
}

interface CouponInfo {
  type: 'coupon';
  coupon_offer_id: string;
  customer_id: string;
  coupon_name: string;
  coupon_header: string;
  coupon_description: string;
  discount_type: string;
  discount_value: number;
  minimum_purchase: number;
  business_id: string;
  business_name: string;
  pass_id: string;
  google_wallet_object_id?: string;
  valid_until?: string;
  can_redeem: boolean;
  already_redeemed: boolean;
  redemption_date?: string;
}

type PassInfo = CustomerLoyaltyInfo | CouponInfo;

interface PassUpdateFormProps {
  customerInfo: PassInfo;
  onUpdate: (action: 'add_points' | 'add_stamps' | 'redeem_reward' | 'redeem_coupon', value?: number) => Promise<void>;
  onCancel: () => void;
  isUpdating: boolean;
}

const PassUpdateForm: React.FC<PassUpdateFormProps> = ({
  customerInfo,
  onUpdate,
  onCancel,
  isUpdating
}) => {
  const [customAmount, setCustomAmount] = useState('');
  const [buttonFlash, setButtonFlash] = useState<string | null>(null);

  const handleQuickAction = async (action: 'add_points' | 'add_stamps' | 'redeem_reward', value?: number) => {
    try {
      // Instant feedback - button flash and haptic
      const flashKey = action === 'add_points' ? `add_points_${value}` : action;
      setButtonFlash(flashKey);
      
      // Haptic feedback for mobile devices
      if ('vibrate' in navigator) {
        navigator.vibrate(200);
      }
      
      // Visual flash effect
      setTimeout(() => setButtonFlash(null), 300);
      
      await onUpdate(action, value);
      
      // Show a brief success toast that doesn't require user interaction
      toast.success("✅ Added!", {
        description: `+${value} ${customerInfo.program_type}! Press more to add additional amounts.`,
      });
    } catch (error) {
      setButtonFlash(null);
      toast.error("❌ Error", {
        description: "Failed to update loyalty pass",
      });
    }
  };

  const handleCustomAmount = async () => {
    const amount = parseInt(customAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Invalid Amount", {
        description: "Please enter a valid positive number",
      });
      return;
    }

    if (customerInfo.program_type === 'points') {
      await handleQuickAction('add_points', amount);
    } else if (customerInfo.program_type === 'stamps') {
      await handleQuickAction('add_stamps', amount);
    }
    
    setCustomAmount('');
  };

  const canRedeemReward = () => {
    if ('program_type' in customerInfo) {
      if (customerInfo.program_type === 'points') {
        return customerInfo.current_points >= 100; // Assuming 100 points needed for reward
      } else if (customerInfo.program_type === 'stamps') {
        return customerInfo.current_stamps >= 10; // Assuming 10 stamps needed for reward
      }
    }
    return false;
  };

  const handleCouponRedeem = async () => {
    try {
      // Instant feedback - button flash and haptic
      setButtonFlash('redeem_coupon');
      
      // Haptic feedback for mobile devices
      if ('vibrate' in navigator) {
        navigator.vibrate(300);
      }
      
      // Visual flash effect
      setTimeout(() => setButtonFlash(null), 300);
      
      await onUpdate('redeem_coupon');
      
      toast.success("🎟️ Coupon Redeemed!", {
        description: "Coupon has been successfully redeemed and marked as used.",
      });
    } catch (error) {
      setButtonFlash(null);
      toast.error("❌ Error", {
        description: "Failed to redeem coupon",
      });
    }
  };

  // Check if this is a coupon
  const isCoupon = customerInfo && 'type' in customerInfo && customerInfo.type === 'coupon';

  return (
    <div className="space-y-6">
      {/* Customer Information Display */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {isCoupon ? 'Coupon Information' : 'Customer Information'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isCoupon ? (
            // Coupon Information Display
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Coupon Name</Label>
                <p className="text-lg font-semibold">{customerInfo.coupon_name}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Customer ID</Label>
                <p className="text-lg font-semibold">{customerInfo.customer_id}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Business</Label>
                <p className="text-lg font-semibold">{customerInfo.business_name}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Discount</Label>
                <p className="text-lg font-semibold text-primary">
                  {customerInfo.discount_type === 'percentage' && `${customerInfo.discount_value}% off`}
                  {customerInfo.discount_type === 'fixed_amount' && `$${customerInfo.discount_value} off`}
                  {customerInfo.discount_type === 'free_item' && 'Free Item'}
                </p>
              </div>
              {customerInfo.minimum_purchase > 0 && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Minimum Purchase</Label>
                  <p className="text-lg font-semibold">${customerInfo.minimum_purchase}</p>
                </div>
              )}
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Status</Label>
                <p className={`text-lg font-semibold ${customerInfo.already_redeemed ? 'text-red-600' : 'text-green-600'}`}>
                  {customerInfo.already_redeemed ? 'Already Redeemed' : 'Available'}
                </p>
              </div>
              {customerInfo.coupon_description && (
                <div className="md:col-span-2">
                  <Label className="text-sm font-medium text-muted-foreground">Description</Label>
                  <p className="text-sm">{customerInfo.coupon_description}</p>
                </div>
              )}
              {customerInfo.redemption_date && (
                <div className="md:col-span-2">
                  <Label className="text-sm font-medium text-muted-foreground">Redeemed Date</Label>
                  <p className="text-sm">{new Date(customerInfo.redemption_date).toLocaleString()}</p>
                </div>
              )}
            </div>
          ) : (
            // Loyalty Pass Information Display
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Customer Name</Label>
                <p className="text-lg font-semibold">{customerInfo.customer_name || 'Unknown'}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Customer ID</Label>
                <p className="text-lg font-semibold">{customerInfo.customer_id}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Program</Label>
                <p className="text-lg font-semibold">{'program_name' in customerInfo ? customerInfo.program_name : 'N/A'}</p>
              </div>
              {'program_type' in customerInfo && customerInfo.program_type === 'points' ? (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Current Points</Label>
                  <p className="text-lg font-semibold text-primary">{customerInfo.current_points}</p>
                </div>
              ) : 'program_type' in customerInfo ? (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Current Stamps</Label>
                  <p className="text-lg font-semibold text-primary">{customerInfo.current_stamps}</p>
                </div>
              ) : null}
              {'total_visits' in customerInfo && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Total Visits</Label>
                  <p className="text-lg font-semibold">{customerInfo.total_visits}</p>
                </div>
              )}
              {'wallet_type' in customerInfo && customerInfo.wallet_type && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Wallet Type</Label>
                  <div className="flex items-center gap-2 text-lg font-semibold">
                    {customerInfo.wallet_type === 'apple' ? (
                      <>
                        <Apple className="h-5 w-5" />
                        <span>Apple Wallet</span>
                      </>
                    ) : (
                      <>
                        <Smartphone className="h-5 w-5" />
                        <span>Google Wallet</span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle>{isCoupon ? 'Coupon Actions' : 'Quick Actions'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isCoupon ? (
            // Coupon Redeem Action
            <div className="space-y-4">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    disabled={isUpdating || customerInfo.already_redeemed || !customerInfo.can_redeem}
                    variant="default"
                    size="lg"
                    className={`w-full flex items-center gap-2 h-16 text-lg font-semibold transition-all duration-300 ${
                      buttonFlash === 'redeem_coupon' 
                        ? 'bg-secondary scale-105 shadow-lg' 
                        : (customerInfo.already_redeemed || !customerInfo.can_redeem)
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-lg'
                    }`}
                  >
                    {isUpdating ? <Loader2 className="h-5 w-5 animate-spin" /> : <Award className="h-5 w-5" />}
                    {customerInfo.already_redeemed ? 'Already Redeemed' : !customerInfo.can_redeem ? 'Cannot Redeem' : 'Redeem Coupon'}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Redeem Coupon</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to redeem this coupon for customer {customerInfo.customer_id}?
                      <br /><br />
                      <strong>Coupon:</strong> {customerInfo.coupon_name}
                      <br />
                      <strong>Discount:</strong> {customerInfo.discount_type === 'percentage' && `${customerInfo.discount_value}% off`}
                      {customerInfo.discount_type === 'fixed_amount' && `$${customerInfo.discount_value} off`}
                      {customerInfo.discount_type === 'free_item' && 'Free Item'}
                      <br />
                      {customerInfo.minimum_purchase > 0 && (
                        <>
                          <strong>Minimum Purchase:</strong> ${customerInfo.minimum_purchase}
                          <br />
                        </>
                      )}
                      <br />
                      This action cannot be undone and the coupon will be marked as used.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleCouponRedeem}>
                      Yes, Redeem Coupon
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              
              {customerInfo.already_redeemed && (
                <p className="text-sm text-red-600 text-center">
                  This coupon has already been redeemed on {new Date(customerInfo.redemption_date || '').toLocaleDateString()}
                </p>
              )}
              
            </div>
          ) : (
            // Loyalty Pass Actions
            <>
              {'program_type' in customerInfo && customerInfo.program_type === 'points' ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <Button
                    onClick={() => handleQuickAction('add_points', 1)}
                    disabled={isUpdating}
                    variant="outline"
                    size="lg"
                    className={`flex items-center gap-2 h-14 text-base font-semibold transition-all duration-300 ${
                      buttonFlash === 'add_points_1' 
                        ? 'bg-secondary text-white scale-105 shadow-lg border-secondary' 
                        : 'border-primary text-primary hover:bg-primary hover:text-white'
                    }`}
                  >
                    {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    +1 Point
                  </Button>
                  
                  <Button
                    onClick={() => handleQuickAction('add_points', 5)}
                    disabled={isUpdating}
                    variant="default"
                    size="lg"
                    className={`flex items-center gap-2 h-14 text-base font-semibold transition-all duration-300 ${
                      buttonFlash === 'add_points_5' 
                        ? 'bg-secondary scale-105 shadow-lg' 
                        : 'bg-primary hover:bg-primary/90 text-white'
                    }`}
                  >
                    {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    +5 Points
                  </Button>
                  
                  <Button
                    onClick={() => handleQuickAction('add_points', 10)}
                    disabled={isUpdating}
                    variant="default"
                    size="lg"
                    className={`flex items-center gap-2 h-14 text-base font-semibold transition-all duration-300 ${
                      buttonFlash === 'add_points_10' 
                        ? 'bg-secondary scale-105 shadow-lg' 
                        : 'bg-primary hover:bg-primary/90 text-white'
                    }`}
                  >
                    {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    +10 Points
                  </Button>
                </div>
              ) : 'program_type' in customerInfo && customerInfo.program_type === 'stamps' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Button
                    onClick={() => handleQuickAction('add_stamps', 1)}
                    disabled={isUpdating}
                    variant="default"
                    size="lg"
                    className={`flex items-center gap-2 h-16 text-lg font-semibold transition-all duration-300 ${
                      buttonFlash === 'add_stamps' 
                        ? 'bg-secondary scale-105 shadow-lg' 
                        : 'bg-primary hover:bg-primary/90 text-white'
                    }`}
                  >
                    {isUpdating ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
                    Add 1 Stamp
                  </Button>
                </div>
              ) : null}

              {/* Redeem Reward Button for Loyalty */}
              {'program_type' in customerInfo && (
                <div className="pt-4 border-t border-gray-200">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        disabled={isUpdating || !canRedeemReward()}
                        variant="default"
                        size="lg"
                        className="w-full flex items-center gap-2 h-14 text-base font-semibold bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-white shadow-lg"
                      >
                        {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Award className="h-4 w-4" />}
                        Redeem Reward
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Redeem Reward</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to redeem a reward for {customerInfo.customer_name || customerInfo.customer_id}?
                          {customerInfo.program_type === 'points' 
                            ? ` This will deduct 100 points from their current balance of ${customerInfo.current_points} points.`
                            : ` This will deduct 10 stamps from their current balance of ${customerInfo.current_stamps} stamps.`
                          }
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleQuickAction('redeem_reward')}>
                          Yes, Redeem Reward
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
              
              {'program_type' in customerInfo && !canRedeemReward() && (
                <p className="text-sm text-muted-foreground text-center">
                  {customerInfo.program_type === 'points' 
                    ? `Need ${100 - customerInfo.current_points} more points to redeem reward`
                    : `Need ${10 - customerInfo.current_stamps} more stamps to redeem reward`
                  }
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Custom Amount - Only show for loyalty passes */}
      {!isCoupon && 'program_type' in customerInfo && (
        <Card>
          <CardHeader>
            <CardTitle>Custom Amount</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col space-y-4">            
              <div className="flex gap-2">
                <Input
                  type="number"
                  placeholder={`Enter ${customerInfo.program_type} amount`}
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="flex-1"
                  min="1"
                />
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      disabled={isUpdating || !customAmount}
                      className="px-6 bg-primary hover:bg-primary/90"
                    >
                      {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Add {customAmount} {customerInfo.program_type}</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to add {customAmount} {customerInfo.program_type} to {customerInfo.customer_name || customerInfo.customer_id}'s loyalty account?
                        Current balance: {customerInfo.program_type === 'points' ? customerInfo.current_points : customerInfo.current_stamps} {customerInfo.program_type}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleCustomAmount}>
                        Yes, Add {customerInfo.program_type}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex gap-4">
        <Button
          onClick={() => {
            onCancel(); // This will reset the form
            // The parent component will handle starting the scanner automatically
          }}
          variant="outline"
          className="flex-1 border-primary text-primary hover:bg-primary hover:text-white"
          disabled={isUpdating}
        >
          Scan Another Customer
        </Button>
      </div>
    </div>
  );
};

export default PassUpdateForm;